import { Color, Container, SvgGraphics, ColorFill } from '@tencent/alloy-renderer';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';
import { InheritElementsLayer } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/master-layout-elements';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { RenderPostionType } from '@src/js/constant';
import { DisplayRule, IFilterDisplayOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/filter/filter-display-scene';
import { SlideElements } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/slide-elements';
import { ISlideComponent, ISlideComponentParams } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/types';
import imageCache from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/fill-style/image-cache';
import { observable } from 'mobx';
import { IPatch } from '@tencent/slide-sdk-constants';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import { IAnimManager } from '@src/slide/application/service/anim-manager/anim-manager.interface';
import { GraphicsRenderComponent } from '@tencent/alloy-graphics';
import { generateGraphicsModel } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/graphic-adapter/generate-graphics-model';
import type ExportImageManager from '@src/slide/application/service/export-image-manager';
import { IRightSideBarManager } from '@src/slide/application/service/right-side-bar-manager/right-side-bar-manager.interface';
import { IAttachmentManager } from '@src/slide/application/service/attachment-upload/attachment-manager.interface';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';
import { IAddonManager } from '@src/slide/application/service/addon/addon-manager.interface';
import {
    IEditorAnimPreviewManager,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/editor-state-animation/editor-anim-preview-manager.interface';
import { getChartsRenderStatusInSlide } from '@src/slide/domain/ui-model/element-modeles/chart-models/status-util';
import { IFormatBrushManager } from '@src/slide/application/service/format-brush/core/interface';
import { IRightSideBarService } from '@src/slide/application/service/right-side-bar-service/core/interface';
import { ILayoutModel } from '@src/slide/domain/ui-model/element-modeles/Interface/layout-model.interface';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';

export class SlideComponent extends Container implements ISlideComponent {
    @observable public slideElementsHasBuilt = false;

    public elementsLayer: Container;
    public options: ISlideComponentParams;
    // 如果已经释放了
    public isDispose: boolean;
    private slideElementsLayer: SlideElements;
    private masterElementsLayer: InheritElementsLayer | null;
    private layoutElementsLayer: InheritElementsLayer | null;
    private backGroundGraphic: GraphicsRenderComponent;
    private whiteBackground: SvgGraphics;
    private masterVariableElementsLayer: InheritElementsLayer | null;
    private layoutVariableElementsLayer: InheritElementsLayer | null;
    private cachedLayoutModel: ILayoutModel;
    private cachedMasterModel: IMasterModel;

    public constructor(params: ISlideComponentParams) {
        super();
        this.options = params;
        this.forceRenderOutsideView = true;

        // 渲染所有模块
        this.renderComponent();
        // 注册所有事件
        this.addAllListener();
        this.options.editLogic?.setComponent(this);
    }
    public getRightSideBarService(): IRightSideBarService | undefined {
        return this.options.rightSideBarService;
    }

    getEditorAnimPreviewManager(): IEditorAnimPreviewManager | undefined {
        return this.options.editorAnimPreviewManager;
    }

    public getUiDataServiceDelegate(): IUiDataService {
        return this.options.uiDataService;
    }

    public getSelectionServiceDelegate(): ISelectionService | undefined {
        return this.options.selectionService;
    }

    public getMediaManagerDelegate(): IMediaManager | undefined {
        return this.options.mediaManager;
    }

    public getAnimManagerDelegate(): IAnimManager | undefined {
        return this.options.animManager;
    }

    public getExportImageManager(): ExportImageManager | undefined {
        return this.options.exportImageManager;
    }

    public getRightSideBarManager(): IRightSideBarManager | undefined {
        return this.options.rightSideBarManager;
    }

    public getAttachmentManager(): IAttachmentManager | undefined {
        return this.options.attachmentManager;
    }

    public getEditingStateManager(): EditingStateManager | undefined {
        return this.options.editingStateManager;
    }

    public getAddonManager(): IAddonManager | undefined {
        return this.options.addonManager;
    }

    public getFormatBrushManager(): IFormatBrushManager | undefined {
        return this.options.formatBrushManager;
    }

    public getMasterVariableElementsLayer(): InheritElementsLayer | null {
        return this.masterVariableElementsLayer;
    }

    public getLayoutVariableElementsLayer(): InheritElementsLayer | null {
        return this.layoutVariableElementsLayer;
    }

    public dispose(): void {
        super.dispose();
        this.isDispose = true;
        if (this.slideElementsLayer) {
            this.slideElementsLayer.dispose();
        }
        this.options.editLogic?.dispose();
        this.removeAllListener();
        // 远程演示缩略图和主视图共用timeline，缩略图卸载时不必卸载动画时间线，编辑区卸载时也不必卸载动画时间线
        if (this.options.slideModel.mainSlideTimeline
            // todo：之前这种写法兜不住，卸载了缩略图后把动画干掉了，看看怎么处理
            && !(this.options.region === RenderPostionType.PRESENTER_VIEW
                || this.options.region === RenderPostionType.CANVAS
                || this.options.region === RenderPostionType.LEFTLIST
            )) {
            this.options.slideModel.mainSlideTimeline.clearMasterTimeline();
            this.options.slideModel.mainSlideTimeline = null;
        }
        this.elementsLayer?.dispose();
        this.masterVariableElementsLayer?.dispose();
        this.layoutVariableElementsLayer?.dispose();
    }

    // 渲染slide背景
    public renderBackground = () => {
        // 右侧背景先接缓存，不走这里
        if (this.options.region === RenderPostionType.CANVAS) {
            return;
        }

        // slide 背景默认渲染白色底色
        this.renderWhiteBackground();
        const slideBgModel = {
            ...this.options.slideModel.props.style,
            width: this.options.uiDataService.presentation.props.slideWidth,
            height: this.options.uiDataService.presentation.props.slideHeight,
            bgShape: ShapeType.rect,
            editable: false,
            selected: false,
            shadeTitleFrame: this.options.slideModel.getBackgroundFill().getShadeTitleFrame(),
        };
        const options: any = {
            getFilterDisplayOptions: this.getFilterDisplayOptions,
            getPageScale: this.options.getPageScale,
            region: this.options.region,
        };
        const backGroundGraphicModel = generateGraphicsModel({
            style: slideBgModel,
            factoryOptions: options,
        });
        if (!this.backGroundGraphic) {
            this.backGroundGraphic = new GraphicsRenderComponent(backGroundGraphicModel);
            this.addChild(this.backGroundGraphic);
        } else {
            this.backGroundGraphic.updateModel(backGroundGraphicModel);
        }
    };

    public get hasBuild(): boolean {
        return this.slideElementsLayer?.hasBuild;
    }

    // 要求立刻渲染，如果还没有创建好需要转同步
    public display(): void {
        if (!this.hasBuild) {
            this.slideElementsLayer.switchBuildMode(true);
        }
    }

    // 确保slideComponent渲染完成
    public async ensureRenderComplete(): Promise<void> {
        // 确保图片资源加载完成
        const imgLoadComplete = imageCache.promiseModelTreeImgLoaded(this.options.slideModel);
        await imgLoadComplete;

        // 确保图表渲染完成
        await this.ensureChartSyncRenderComplete();

        // 强制同步渲染一次
        this.options.getApplication?.().render();
    }

    public getFilterDisplayOptions = (): IFilterDisplayOptions
        | undefined => (this.options.region === RenderPostionType.LEFTLIST ? {
        shadow: DisplayRule.None,
        glow: DisplayRule.None,
        softEdge: DisplayRule.None,
        reflection: DisplayRule.None,
        image: DisplayRule.None,
    } : undefined);

    public refreshBackground = (): void => {
        this.renderBackground();
        this.options.renderEditorBackground?.();
    };

    protected onDidRender(): void {
        super.onDidRender();
        this.options.onDidRenderCallback?.(this);
        this.options.editLogic?.onDidRender?.();
    }

    private ensureChartSyncRenderComplete() {
        const renderList = getChartsRenderStatusInSlide(this.options.slideModel);
        return Promise.all(renderList);
    }

    private addAllListener = () => {
        this.options.slideModel.changeSetEngine.addChangesetObserver(this.renderComponent);
        this.onClick((e) => {
            this.options.handleSlideClick?.(e, this.options.slideModel);
        });
    };

    private removeAllListener = () => {
        this.options.slideModel.changeSetEngine.removeChangesetObserver(this.renderComponent);
    };

    // 渲染所有slide模块
    private renderComponent = (changesetInfo?: IPatch) => {
        this.options.onWillRenderCallback?.(this);
        // 渲染slide背景
        this.renderBackground();
        // 渲染master和layout
        this.renderMasterLayout(changesetInfo);
        this.renderMasterLayoutVariableElements();
        // 渲染slide内图形元素
        this.renderElements();
    };

    // 渲染slide内图形元素
    private renderElements = () => {
        if (!this.slideElementsLayer) {
            this.slideElementsLayer = new SlideElements(this, this.options.asyncBuild);
        }
        this.slideElementsLayer.updateComponent();
    };

    /**
     * 渲染母版和版式上的可变元素
     */
    private renderMasterLayoutVariableElements = () => {
        // 只有幻灯片和版式才需要渲染master
        if (this.options.slideModel.isSlide || this.options.slideModel.isLayout) {
            if (!this.masterVariableElementsLayer) {
                this.masterVariableElementsLayer = new InheritElementsLayer(
                    UIModelType.MASTER,
                    this.options.slideModel,
                    this.options.getPageScale,
                    this.options.getApplication,
                    this.options.region,
                    this.options.fromScale,
                    {
                        infrastructureService: this.options.infrastructureService,
                        uiDataService: this.options.uiDataService,
                        mediaManager: this.options.mediaManager,
                        linkManager: this.options.linkManager,
                        editingStateManager: this.options.editingStateManager,
                        formatBrushManager: this.options.formatBrushManager,
                        fontManager: this.options.fontManager,
                        masterLayoutEditService: this.options.masterLayoutEditService,
                        gifService: this.options.gifService,
                        selectionService: this.options.selectionService,
                    },
                    undefined,
                    true,
                );
                this.addChild(this.masterVariableElementsLayer);
            }
            this.masterVariableElementsLayer.updateSlideModel(this.options.slideModel, UIModelType.MASTER);
        }
        // 只有幻灯片才需要渲染layout
        if (this.options.slideModel.isSlide) {
            if (!this.layoutVariableElementsLayer) {
                this.layoutVariableElementsLayer = new InheritElementsLayer(
                    UIModelType.LAYOUT,
                    this.options.slideModel,
                    this.options.getPageScale,
                    this.options.getApplication,
                    this.options.region,
                    this.options.fromScale,
                    {
                        infrastructureService: this.options.infrastructureService,
                        uiDataService: this.options.uiDataService,
                        mediaManager: this.options.mediaManager,
                        linkManager: this.options.linkManager,
                        editingStateManager: this.options.editingStateManager,
                        formatBrushManager: this.options.formatBrushManager,
                        fontManager: this.options.fontManager,
                        masterLayoutEditService: this.options.masterLayoutEditService,
                        gifService: this.options.gifService,
                        selectionService: this.options.selectionService,
                    },
                    undefined,
                    true,
                );
                this.addChild(this.layoutVariableElementsLayer);
            }
            this.layoutVariableElementsLayer.updateSlideModel(this.options.slideModel, UIModelType.LAYOUT);
        }
    };

    // 渲染master和layout
    /* eslint-disable complexity */
    private renderMasterLayout = (changesetInfo?: IPatch) => {
        // 右侧先接缓存
        if (this.options.region === RenderPostionType.CANVAS) {
            if (changesetInfo) {
                // 支持强制刷新组件，即使没有变化
                this.options.forceReloadWithoutSelfChange?.(changesetInfo);
            }
            return;
        }
        // 只有幻灯片和版式才需要渲染master
        if (this.options.slideModel.isSlide || this.options.slideModel.isLayout) {
            const newMasterModel = this.options.slideModel.getRelationMasterModel();
            if (newMasterModel !== this.cachedMasterModel || !this.masterElementsLayer) {
                const masterElementsLayer = new InheritElementsLayer(
                    UIModelType.MASTER,
                    this.options.slideModel,
                    this.options.getPageScale,
                    this.options.getApplication,
                    this.options.region,
                    this.options.fromScale,
                    {
                        infrastructureService: this.options.infrastructureService,
                        uiDataService: this.options.uiDataService,
                        mediaManager: this.options.mediaManager,
                        linkManager: this.options.linkManager,
                        editingStateManager: this.options.editingStateManager,
                        formatBrushManager: this.options.formatBrushManager,
                        fontManager: this.options.fontManager,
                        masterLayoutEditService: this.options.masterLayoutEditService,
                        gifService: this.options.gifService,
                        selectionService: this.options.selectionService,
                    },
                );

                if (this.masterElementsLayer) {
                    const index = this.children.findIndex(obj => obj === this.masterElementsLayer);
                    this.removeChild(this.masterElementsLayer);
                    this.addChildAt(masterElementsLayer, index);
                } else {
                    this.addChild(masterElementsLayer);
                }
                this.masterElementsLayer = masterElementsLayer;
            }
            this.cachedMasterModel = newMasterModel;
            this.masterElementsLayer?.updateSlideModel(this.options.slideModel, UIModelType.MASTER);
        }
        // 只有幻灯片才需要渲染layout
        if (this.options.slideModel.isSlide) {
            const newLayoutModel = this.options.slideModel.getRelationLayoutModel();
            if (newLayoutModel !== this.cachedLayoutModel || !this.layoutElementsLayer) {
                const layoutElementsLayer = new InheritElementsLayer(
                    UIModelType.LAYOUT,
                    this.options.slideModel,
                    this.options.getPageScale,
                    this.options.getApplication,
                    this.options.region,
                    this.options.fromScale,
                    {
                        infrastructureService: this.options.infrastructureService,
                        uiDataService: this.options.uiDataService,
                        mediaManager: this.options.mediaManager,
                        linkManager: this.options.linkManager,
                        editingStateManager: this.options.editingStateManager,
                        formatBrushManager: this.options.formatBrushManager,
                        fontManager: this.options.fontManager,
                        masterLayoutEditService: this.options.masterLayoutEditService,
                        gifService: this.options.gifService,
                        selectionService: this.options.selectionService,
                    },
                );

                if (this.layoutElementsLayer) {
                    const index = this.children.findIndex(obj => obj === this.layoutElementsLayer);
                    this.removeChild(this.layoutElementsLayer);
                    this.addChildAt(layoutElementsLayer, index);
                } else {
                    this.addChild(layoutElementsLayer);
                }
                this.layoutElementsLayer = layoutElementsLayer;
            }
            this.cachedLayoutModel = newLayoutModel;
            this.layoutElementsLayer?.updateSlideModel(this.options.slideModel, UIModelType.LAYOUT);
        }
    };

    private renderWhiteBackground() {
        const { slideWidth, slideHeight } = this.options.uiDataService.presentation.props;
        const path = `M 0 0 L ${slideWidth} 0 L ${slideWidth} ${slideHeight} L 0 ${slideHeight} Z`;
        if (!this.whiteBackground) {
            this.whiteBackground = new SvgGraphics(path);
            this.whiteBackground.fill = new ColorFill(Color.White);
            this.addChild(this.whiteBackground);
        } else {
            this.whiteBackground.path = path;
        }
    }
}
